import { ReactElement, useEffect } from 'react';
import { KWELLO_ICON, KwelloSvgIcon } from '../icon/KwelloSvgIcon';
import ClickableDiv from '../clickableDiv/ClickableDiv';
import { Button } from '@progress/kendo-react-buttons';
import { useResize } from 'common/hooks/resize';

interface SlideMenuProps {
  showMenu: boolean;
  setShowMenu: (dialogOpen: boolean) => void;
  title?: string;
  children?: any;
  width?: string;
  height?: string;
  onClickGoBack?: () => void;
  onClose?: () => void;
  titleIcon?: ReactElement;
  slideDirection?: string;
  hideBorder?: boolean;
  className?: string;
  hideClose?: boolean;
}

export const SlideMenu = ({
  showMenu,
  setShowMenu,
  title,
  children,
  width,
  height,
  onClickGoBack,
  onClose,
  titleIcon,
  slideDirection,
  hideBorder,
  className,
  hideClose = false,
}: SlideMenuProps) => {
  const { isMobile, isTabletOrMobile } = useResize();

  useEffect(() => {
    const { Intercom } = window;
    if (Intercom) {
      Intercom('update', {
        hide_default_launcher: showMenu,
      });
    }
  }, [showMenu]);

  const getTitle = () => {
    if (titleIcon) {
      return (
        <>
          <span className={'title-icon'}> {titleIcon}</span>
          <h3>{title}</h3>
        </>
      );
    }
    if (title) {
      return <h3>{title}</h3>;
    }
    return null;
  };

  const headerType = () => {
    if (onClickGoBack) {
      return 'go-back-header';
    }

    if (!isMobile && isTabletOrMobile) {
      return 'tablet-header';
    }

    return 'header';
  };

  return (
    <ClickableDiv
      className={showMenu ? 'slide-menu-overlay' : ''}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        const isOverlay = target.classList.contains('slide-menu-overlay');
        if (isOverlay && onClose) {
          onClose();
        }
      }}
    >
      <div
        className={`${className} ${slideDirection === 'left' ? 'flyout-left' : 'flyout-right'}`}
        style={
          !showMenu
            ? {
                [slideDirection ?? 'right']: `-${width ?? '100%'}`,
                height,
                maxWidth: width ?? '0',
                overflow: 'hidden',
              }
            : { height, maxWidth: width ?? '100%' }
        }
      >
        <div className={headerType()}>
          {onClickGoBack ? (
            <>
              <Button fillMode="flat" onClick={onClickGoBack} size="small">
                <KwelloSvgIcon icon={KWELLO_ICON.BACK_ICON} />
              </Button>
              {title && <div>{title}</div>}
            </>
          ) : (
            getTitle()
          )}
          {!hideClose && (
            <ClickableDiv
              className={'close-icon'}
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  setShowMenu(false);
                }
              }}
            >
              <KwelloSvgIcon icon={KWELLO_ICON.POPUP_CLOSE_ICON} />
            </ClickableDiv>
          )}
        </div>
        {!hideBorder && <div className={'border'} />}
        {children}
      </div>
    </ClickableDiv>
  );
};
